import {useQuery} from "@tanstack/react-query";
import {Sensor, getEvents} from "../api";
import {FiletypeCsv} from 'react-bootstrap-icons';
import {Button, Image, Modal, Form} from "react-bootstrap";
import Moment from "react-moment";
import PropTypes from "prop-types";
import React, {useState, useEffect} from "react";
import {NonIdealState, Spinner} from "@blueprintjs/core";
import sensors from "../pages/Sensors";
import moment from "moment";

moment.locale('en', {
    longDateFormat : {
        LT: "h:mm:ss A", // <----------- add :ss
        L: "MM/DD/YYYY",
        l: "M/D/YYYY",
        LL: "MMMM Do YYYY",
        ll: "MMM D YYYY",
        LLL: "MMMM Do YYYY LT",
        lll: "MMM D YYYY LT",
        LLLL: "dddd, MMMM Do YYYY LT",
        llll: "ddd, MMM D YYYY LT"
    }
});

// Check if sensor is on fence mode
function isFenceMode(sysStatus){
    let fence_mode;
    const convert2bin = (sysStatus >>> 0).toString(2).split('');

    if(convert2bin[convert2bin.length - 10] === '0' || convert2bin[convert2bin.length - 10] === undefined){
        fence_mode = false;
    } else {
        fence_mode = true;
    }

    return fence_mode
}

function filterOn(sysStatus, detectionType) {
    let filter = [];
    const convert2bin = (sysStatus >>> 0).toString(2).split('');
    let reverse_idx = [9,8,7,5];
    let status_bit = [];
    for (const [idx, value] of reverse_idx.entries()){
        if(convert2bin[convert2bin.length - value] === '0' || convert2bin[convert2bin.length - value] === undefined){
            status_bit.push('0')
        } else {
            status_bit.push('1')
        }
    }

    //status_bit[0] => Step Detector prefilter, system_status bit 9
    //status_bit[1] => NN prefilter, system_status bit 8
    //status_bit[2] => Threshold prefilter, system_status bit 7
    //status_bit[3] => Kurtosis prefilter, system_status bit 5

    if (status_bit[0] === '0'){ //Step Detector Unused
        // Passed  ALL for ENVINROMENT, VEHICLE, ANIMAL => no filter, show % Confidence
        if(status_bit[1] === '1' && status_bit[2] === '1' && status_bit[3] === '1'){
            return filter
        // Threshold and Kurtosis Used but Failed Kurtosis
        } else if (status_bit[1] === '0' && status_bit[2] === '1' && status_bit[3] === '1'){
            filter.push('Kurtosis Prefilter');
            return filter
        // Threshold Used but Failed
        } else if (status_bit[1] === '0' && status_bit[2] === '1' && status_bit[3] === '0'){
            filter.push('Threshold Prefilter');
            return filter
        } else {
            //Case of unexpected result
            filter.push('error')
            console.log('Error: system status filter')
            return filter
        }
    } else { //Step Detector Passes
        //if Step Detector Passes, all other filters are passed and DetectionType is Human (2) => No filter to display, Show confidence #%
        if(status_bit[1] === '1' && status_bit[2] === '1' && status_bit[3] === '1'){
            // If All filters are passed, but DetectionType is Environment (0) (meaning Failed by Step Detector) => Show "Filter: Step Detector Filter" and Environment Label
            if(detectionType === 0){
                filter.push('Step Detector Filter');
            }
            return filter
        }
        else if(status_bit[1] !== '1' || status_bit[2] !== '1' || status_bit[3] !== '1'){
            //Case of unexpected result
            filter.push('error')
            console.log('Error: system status filter')
            return filter
        }
    }
    filter.push('error')
    console.log('Error: system status filter')
    return filter
}

function isFilterOn(sysStatus, detectionType){
    return filterOn(sysStatus, detectionType).length > 0;
}

// function to return the Gateway Device Id from filtering the Gateway Id (database index id)
function getGatewayDeviceId(gateways, gatewayId){
    let gatewayDeviceId = gateways.filter(gateway => gateway.id === gatewayId)
    return gatewayDeviceId[0]?.json.gatewayId
}

function EventView(props){
    const {selectedSensor, organizationId, gateways} = props;

    const [limit, setLimit] = useState(10);
    const [imageViewer, setImageViewer] = useState(false);
    const [image, setImage] = useState();
    const [ isHour24, setIsHour24 ] = useState(true)

    const eventQuery = useQuery({
        queryKey: [`events`, organizationId, selectedSensor.id, limit],
        queryFn: (arg) => {
            return getEvents(organizationId, limit, selectedSensor.id);
        },
        refetchOnWindowFocus: 'true',
        refetchInterval: 5000
    });

    useEffect(() => setLimit(10), [JSON.stringify({id:selectedSensor.id, type:selectedSensor.deviceType})]);

    const dataAvailable = (eventQuery.status === 'success' && eventQuery.data.events);
    const numberOfRecords = dataAvailable ? eventQuery.data.events.length : null;

    const handleOpenImageViewer = (imageUrl) => {
        setImageViewer(true)
        setImage(imageUrl)
    }
    const handleCloseImageViewer = () => {
        setImageViewer(false)
    }

    if(dataAvailable && numberOfRecords > 0) {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timeZoneAbbr = new Date().toLocaleTimeString('en-us', {
            timeZoneName: 'short',
        }).split(' ').pop();
        return (
            <div>
                <div>
                    <Form.Check
                        type="switch"
                        id="custom-switch"
                        label={"24-Hour Time"}
                        checked={isHour24}
                        onChange={()=>{setIsHour24(!isHour24)}}
                    />
                    <hr/>
                </div>
                {
                    eventQuery.data.events.map(
                        event =>
                            <div key={event.id} className="detection">
                                <div>
                                    {isFenceMode(event?.json.sensorStatus?.systemStatus) && event?.json.detectionType !== 5 ? 
                                    // IF Fence mode is ON and the detectionType is NOT 5=='Fence Intrusion' => Detection override to 'Environment'
                                        isHour24 ? 
                                        <><b>Environment</b>: <Moment format="MM/DD/YYYY [at] HH:mm:ss">{event.timestamp}</Moment> {timeZoneAbbr}</>
                                        :
                                        <><b>Environment</b>: <Moment format="MM/DD/YYYY [at] h:mm:ss A">{event.timestamp}</Moment> {timeZoneAbbr}</>
                                    :
                                        isHour24 ?
                                        <><b>{event.label}</b>: <Moment format="MM/DD/YYYY [at] HH:mm:ss">{event.timestamp}</Moment> {timeZoneAbbr}</>
                                        :
                                        <><b>{event.label}</b>: <Moment format="MM/DD/YYYY [at] h:mm:ss A">{event.timestamp}</Moment> {timeZoneAbbr}</>
                                    }
                                </div>
                                <div className="latency">
                                    <div>Latency: {event.latency !== null && event.latency > 0 ?
                                        <span>{event.latency} sec </span> : <span>--</span>}</div>
                                    <div>Battery: {event.battery !== null ? <span>{event.battery} %</span> :
                                        <span>--</span>}</div>
                                    {/* If Fence mode is OFF display Filter/Confidence, otherwise Null */}
                                    {isFenceMode(event?.json.sensorStatus?.systemStatus) ? 
                                        null
                                    :
                                        (isFilterOn(event?.json.sensorStatus?.systemStatus, event?.json.detectionType) ? 
                                            <div>Filter: {filterOn(event?.json.sensorStatus?.systemStatus, event?.json.detectionType)[0]} </div> 
                                            : (event.confidence !== null) ? 
                                            <div>Confidence: {event.confidence} %</div> 
                                            :<div>Confidence: --</div>)
                                    }
                                </div>
                                <div className="latency">
                                    <div>Via Gateway: {event.gatewayId !== null ?
                                        <span>{getGatewayDeviceId(gateways, event.gatewayId)}</span> : null}</div>
                                </div>
                                <div className="latency">
                                    <div>
                                        RSSI Up: {
                                            event.rssiUp !== null ?
                                                <span>{event.rssiUp} dBm </span>
                                                : <span>--</span>
                                        }
                                    </div>
                                    <div>
                                        RSSI Down: {
                                            event.rssiDown !== null ?
                                                <span>{event.rssiDown} dBm </span>
                                                : <span>--</span>
                                        }
                                    </div>
                                </div>
                                <div className="file-row">
                                    {event.json.files.map(file => {
                                        if (file.type === 'IMAGE'){
                                            return (
                                                <div key={`button-image-${file.id}`} onClick={()=>handleOpenImageViewer(`/organization/${organizationId}/file/${file.id}`)}>
                                                    <Image 
                                                        key={`image-${file.id}`} 
                                                        src={`/organization/${organizationId}/file/${file.id}`}
                                                        style={{
                                                            objectFit:'contain',
                                                            height:'100px',
                                                            width:'100px'
                                                        }}
                                                        thumbnail
                                                    />
                                                </div>
                                            )
                                        }
                                    })}
                                    {event.json.files.map(file => {
                                        if (file.type === 'CSV'){
                                            return <div key={`csv-${file.id}`} href={`/organization/${organizationId}/file/${file.id}?download=true`} style={{border:'1px solid rgb(222, 226, 230)', borderRadius:'4px', height:'100px', width:'100px', display:'flex', justifyContent:'center', alignItems:'center'}}>
                                                <a href={`/organization/${organizationId}/file/${file.id}?download=true`} style={{margin:'0', textDecoration:'none'}}>{file.type}</a>
                                            </div>
                                        }
                                    })}
                                    {event.json.files.map(file => {
                                        if (file.type !== 'CSV' && file.type !== 'IMAGE'){
                                            return <div key={`file-${file.id}`} href={`/organization/${organizationId}/file/${file.id}?download=true`} style={{border:'1px solid rgb(222, 226, 230)', borderRadius:'4px', height:'100px', width:'100px', display:'flex', justifyContent:'center', alignItems:'center'}}>
                                                <a href={`/organization/${organizationId}/file/${file.id}?download=true`} style={{margin:'0', textDecoration:'none'}}>{file.type}</a>
                                            </div>
                                        }
                                    })}
                                </div>
                            </div>
                    )
                }

                <Modal show={imageViewer} onHide={handleCloseImageViewer} size='lg'>
                    <Modal.Header closeButton>
                    <Modal.Title>Image</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div style={{display:'flex', justifyContent:'center', alignItems:'center'}}>
                            <Image 
                                key={`image-zoom`} 
                                src={image}
                                style={{
                                    objectFit:'contain',
                                    width:'100%'
                                }}
                                rounded
                            />
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseImageViewer}>
                        Close
                    </Button>
                    <Button variant="primary" href={`${image}?download=true`}>
                        Download
                    </Button>
                    </Modal.Footer>
                </Modal>

                {
                    (numberOfRecords === limit) ?
                    <div className="text-center">
                        <Button variant="link" onClick={() => setLimit(lim => lim + 10)}> show more </Button>
                    </div> : null
                }

                <div className="csv-download">
                    <a href={`/organization/${organizationId}/events/?limit=40000&sensorId=${selectedSensor.id}&format=csv&mask=false`}>
                        <Button variant="secondary" size="sm"><FiletypeCsv/> CSV</Button>
                    </a>
                </div>
            </div>
        );
    } else if (dataAvailable) {
        return (
            <NonIdealState
                title={"No Events Detected"}
                description={`No events were detected for sensor ${selectedSensor.deviceId}.`}
            />
        )
    } else {
        return <div><Spinner /></div>;
    }
}

EventView.propTypes = {
    selectedSensor: PropTypes.object,
    organizationId: PropTypes.number,
    gateways: PropTypes.object,
}

export {isFenceMode}

export default EventView;